/*
 * VOLTAJE · La corriente
 * Dibuja un rayo dorado que pasa por los puntos de cada valor (sections/voltaje-corriente.liquid).
 * El trazo se calcula a partir de la posición real de los puntos, así que se adapta
 * a escritorio (horizontal), móvil (vertical) y a cualquier número de valores.
 * La animación en sí está en CSS; aquí solo se crean los trazos y se decide cuándo empieza.
 */
(() => {
  if (customElements.get('vj-current')) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const DRAW_MS = 1500; // debe coincidir con --vj-draw en el CSS de la sección
  const BRANCH_GAP_MS = 40; // las ramas brotan justo después de encenderse el punto
  const SEGMENT_PX = 26; // longitud media de cada quiebro del rayo
  const AMPLITUDE_PX = 7; // cuánto se desvía el rayo de la línea recta

  // Aleatorio con semilla: el rayo tiene siempre la misma forma en cada visita.
  const seeded = (seed) => () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const distance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

  // Tramo quebrado entre dos puntos; los extremos quedan fijos para que el rayo pase por los nodos.
  const jagged = (a, b, amplitude, segment, rand) => {
    const length = distance(a, b);
    const steps = Math.max(2, Math.round(length / segment));
    const nx = -(b.y - a.y) / length;
    const ny = (b.x - a.x) / length;
    const points = [];
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const offset = (rand() * 2 - 1) * amplitude * Math.sqrt(Math.sin(Math.PI * t));
      points.push({ x: a.x + (b.x - a.x) * t + nx * offset, y: a.y + (b.y - a.y) * t + ny * offset });
    }
    points.push(b);
    return points;
  };

  const toPath = (points) =>
    points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

  const createPath = (className, d, extra = {}) => {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('class', className);
    path.setAttribute('d', d);
    path.setAttribute('pathLength', '1');
    for (const [key, value] of Object.entries(extra)) path.setAttribute(key, value);
    return path;
  };

  const createBlur = (id, deviation) => {
    const filter = document.createElementNS(SVG_NS, 'filter');
    filter.setAttribute('id', id);
    // Región en coordenadas del lienzo: un rayo casi recto tiene una caja de altura ~0
    // y el desenfoque se recortaría si la región fuera relativa a esa caja.
    filter.setAttribute('filterUnits', 'userSpaceOnUse');
    filter.setAttribute('x', '-4000');
    filter.setAttribute('y', '-4000');
    filter.setAttribute('width', '12000');
    filter.setAttribute('height', '12000');
    const blur = document.createElementNS(SVG_NS, 'feGaussianBlur');
    blur.setAttribute('stdDeviation', deviation);
    filter.append(blur);
    return filter;
  };

  let instances = 0;

  class VjCurrent extends HTMLElement {
    connectedCallback() {
      this.uid = `vj-current-${++instances}`;
      this.stage = this.querySelector('.vj-current__stage');
      this.svg = this.querySelector('.vj-current__svg');
      if (!this.stage || !this.svg) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
      this.canAnimate =
        this.hasAttribute('data-animate') && !reduceMotion && !inEditor && 'IntersectionObserver' in window;

      if (this.canAnimate) this.dataset.armed = '';
      this.build();

      this.lastWidth = this.stage.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.stage.clientWidth === this.lastWidth) return;
        this.lastWidth = this.stage.clientWidth;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.build(), 150);
      });
      this.resizeObserver.observe(this.stage);

      if (!this.canAnimate) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            delete this.dataset.paused;
            if (!('live' in this.dataset)) this.start();
          } else if ('live' in this.dataset) {
            // Fuera de pantalla el pulso de luz se detiene: no gasta batería.
            this.dataset.paused = '';
          }
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.resizeTimer);
      clearTimeout(this.doneTimer);
    }

    start() {
      this.dataset.live = '';
      // Al terminar, el estado queda fijo: si se redimensiona la ventana el rayo no se vuelve a dibujar.
      // En modo tierra se espera más: las raíces crecen después de que llegue la corriente.
      const settle = this.dataset.mode === 'ground' ? 1900 : 1300;
      this.doneTimer = setTimeout(() => {
        this.dataset.done = '';
      }, DRAW_MS + settle);
    }

    build() {
      const nodes = [...this.querySelectorAll('[data-vj-node]')];
      if (!nodes.length) return;

      const rand = seeded(Number(this.dataset.seed) || 7);
      const stageRect = this.stage.getBoundingClientRect();
      const centers = nodes.map((node) => {
        const r = node.getBoundingClientRect();
        return { x: r.left + r.width / 2 - stageRect.left, y: r.top + r.height / 2 - stageRect.top };
      });

      const first = centers[0];
      const last = centers[centers.length - 1];
      // Modo tierra: recorrido vertical que termina en el último punto (el suelo) y echa raíces.
      const ground = this.dataset.mode === 'ground';
      const horizontal = !ground && (centers.length < 2 || Math.abs(last.y - first.y) < 4);

      // En horizontal la corriente atraviesa la pantalla de borde a borde.
      const start = horizontal ? { x: -stageRect.left, y: first.y } : { x: first.x, y: 0 };
      const end = horizontal
        ? { x: document.documentElement.clientWidth - stageRect.left, y: last.y }
        : { x: last.x, y: this.stage.clientHeight };

      // Trazo principal y en qué fracción del recorrido cae cada punto.
      // En modo tierra el rayo acaba en el suelo: de ahí en adelante siguen las raíces.
      const anchors = ground ? [start, ...centers] : [start, ...centers, end];
      const points = [start];
      const nodeFractions = [];
      let travelled = 0;
      const lengths = [];
      for (let i = 1; i < anchors.length; i++) {
        const segmentPoints = jagged(anchors[i - 1], anchors[i], AMPLITUDE_PX, SEGMENT_PX, rand);
        let prev = points[points.length - 1];
        for (const p of segmentPoints) {
          travelled += distance(prev, p);
          points.push(p);
          prev = p;
        }
        lengths.push(travelled);
      }
      for (let i = 0; i < centers.length; i++) nodeFractions.push(lengths[i] / travelled);

      const blurId = `${this.uid}-blur`;
      const softBlurId = `${this.uid}-blur-soft`;
      const defs = document.createElementNS(SVG_NS, 'defs');
      defs.append(createBlur(blurId, 4), createBlur(softBlurId, 1.5));

      const mainD = toPath(points);
      const fragment = document.createDocumentFragment();
      fragment.append(defs, createPath('vj-current__glow', mainD, { filter: `url(#${blurId})` }));

      centers.forEach((center, i) => {
        const at = Math.round(nodeFractions[i] * DRAW_MS);
        nodes[i].closest('li')?.style.setProperty('--vj-at', `${at}ms`);

        // Ramificaciones hacia arriba, como raíces o descargas (solo en horizontal, donde hay hueco).
        if (!horizontal) return;
        const count = 2 + Math.round(rand());
        for (let b = 0; b < count; b++) {
          const side = b % 2 === 0 ? -1 : 1;
          const angle = -Math.PI / 2 + side * (0.25 + rand() * 0.55);
          const length = 28 + rand() * 34;
          const tip = { x: center.x + Math.cos(angle) * length, y: center.y + Math.sin(angle) * length };
          const branch = [center, ...jagged(center, tip, 4, 9, rand)];
          const delay = at + BRANCH_GAP_MS + b * 60;
          fragment.append(createPath('vj-current__branch', toPath(branch), { style: `--vj-at:${delay}ms` }));

          // Una sub-rama corta desde la mitad de la rama principal.
          if (rand() > 0.4) {
            const mid = branch[Math.floor(branch.length / 2)];
            const subAngle = angle + side * (0.35 + rand() * 0.4);
            const subLength = length * (0.35 + rand() * 0.2);
            const subTip = { x: mid.x + Math.cos(subAngle) * subLength, y: mid.y + Math.sin(subAngle) * subLength };
            fragment.append(
              createPath('vj-current__branch', toPath([mid, ...jagged(mid, subTip, 3, 8, rand)]), {
                style: `--vj-at:${delay + 120}ms`,
              })
            );
          }
        }
      });

      // Modo tierra: al llegar la corriente al suelo se abre un sistema de raíces hacia abajo,
      // como el árbol del logo. Cada raíz principal puede echar una o dos secundarias.
      if (ground) {
        const ROOTS = 6;
        // Abanico asimétrico: el texto del último paso está a la derecha del punto,
        // así que las raíces se abren hacia abajo y a la izquierda (hasta 70°) y solo un poco a la derecha (20°).
        const MIN_ANGLE = Math.PI / 2 - (20 * Math.PI) / 180;
        const MAX_ANGLE = Math.PI / 2 + (70 * Math.PI) / 180;
        const clampAngle = (a) => Math.min(MAX_ANGLE + 0.25, Math.max(MIN_ANGLE - 0.1, a));
        const arrival = DRAW_MS + 60;
        for (let r = 0; r < ROOTS; r++) {
          const t = ROOTS === 1 ? 0.5 : r / (ROOTS - 1);
          const angle = MIN_ANGLE + t * (MAX_ANGLE - MIN_ANGLE) + (rand() - 0.5) * 0.12;
          const length = 62 + rand() * 70 - Math.abs(t - 0.5) * 30; // las centrales, más largas
          const tip = { x: last.x + Math.cos(angle) * length, y: last.y + Math.sin(angle) * length };
          const root = [last, ...jagged(last, tip, 5, 12, rand)];
          const delay = arrival + Math.round(Math.abs(t - 0.5) * 2 * 160); // del centro hacia fuera
          fragment.append(createPath('vj-current__root', toPath(root), { style: `--vj-at:${delay}ms` }));

          const subs = 1 + Math.round(rand());
          for (let s = 0; s < subs; s++) {
            const from = root[Math.min(root.length - 2, Math.floor(root.length * (0.35 + rand() * 0.3)))];
            const side = rand() > 0.5 ? 1 : -1;
            const subAngle = clampAngle(angle + side * (0.35 + rand() * 0.45));
            const subLength = length * (0.3 + rand() * 0.25);
            const subTip = { x: from.x + Math.cos(subAngle) * subLength, y: from.y + Math.sin(subAngle) * subLength };
            fragment.append(
              createPath('vj-current__root', toPath([from, ...jagged(from, subTip, 3, 9, rand)]), {
                style: `--vj-at:${delay + 220 + s * 90}ms`,
              })
            );
          }
        }
      }

      fragment.append(
        createPath('vj-current__bolt', mainD),
        createPath('vj-current__spark', mainD, { filter: `url(#${softBlurId})` })
      );

      this.svg.replaceChildren(fragment);
    }
  }

  customElements.define('vj-current', VjCurrent);
})();
